
import { validPassengers } from '@/composables/useBookingValidation';
import useModeOfTravel from '@/composables/useModeOfTravel';
import useRoutes from '@/composables/useRoutes';
import useRuntimeConfig from '@/composables/useRuntimeConfig';
import { typeOfTravel } from '@/config/typeOfTravel';
import { passengerValidationTexts } from '@/helpers/validationTextHelper';
import { DropdownOption } from '@/models/DropdownOption';
import { DirectionEnum } from '@/models/enums';
import { stateStorage } from '@/services/storage.service';
import { useBookingStore } from '@/store/booking';
import { useCampaignStore } from "@/store/campaign";
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useMediaQuery } from '@vueuse/core';
import { computed, defineComponent, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { datePart, today } from "@/helpers/dateHelper";
import { useRouter } from 'vue-router';
import BaseAlert from '../base/BaseAlert.vue';
import BaseButton from '../base/BaseButton.vue';
import BaseDropdown from '../base/BaseDropdown.vue';
import QuickformCalendar from './QuickformCalendar.vue';
import QuickformDiscount from './QuickformDiscount.vue';
import QuickformPassengers from './QuickformPassengers.vue';
export default defineComponent({
	components: {
		BaseDropdown,
		QuickformCalendar,
		BaseButton,
		QuickformPassengers,
		BaseAlert,
		QuickformDiscount
	},
	props: {
		isExternal: Boolean
	},
	setup(props) {
		const { selectMode } = useModeOfTravel();
		const { hasTo, hasFrom, from, to, returnDestinations, outwardDestinations, destinationNames, disableRoute } = useRoutes();
		const bookingStore = useBookingStore();
		const campaignService = useCampaignStore();
		const { getBooking } = useBookingStore();
		const { getRuntimeConfig } = useRuntimeConfig();
		const isMediumScreen = useMediaQuery('(max-width: 960px) and (min-width: 780px)');
		const isSmallScreen = useMediaQuery('(max-width: 780px)');
		const quickformRef = ref();
		const quickformAction = ref();
		const bookingState = ref();
		const router = useRouter();
		
		/* for external */
		const passengers = computed(() => 
			bookingStore.getPassengers(DirectionEnum.Outward)
			.map((pax) => pax.type).join(''))
  		const outwardDate = computed(() => datePart(bookingStore.getDate(DirectionEnum.Outward) ?? today()))
		const returnDate = computed(() => datePart(bookingStore.getDate(DirectionEnum.Return) ?? today()))
		const voucherCode = computed(() => campaignService.getState.code);
  		
		const loading = ref(false);

		const outwardOptions = computed(() =>
			outwardDestinations.value.map(
				(v) =>
					({
						label: (destinationNames.value as any)[v.code],
						value: v.code
					} as DropdownOption)
			)
		);

		const returnOptions = computed(() =>
			returnDestinations.value.map(
				(v) =>
					({
						label: (destinationNames.value as any)[v.code],
						value: v.code,
						disabled: disableRoute(v.code)
					} as DropdownOption)
			)
		);

		const state = reactive({
			modeOfTravel: bookingStore.getBooking.modeOfTravel,
			leavingFrom: from.value,
			goingTo: to.value,
			dates: {
				outward: bookingStore.getDate(DirectionEnum.Outward),
				return: bookingStore.getDate(DirectionEnum.Return)
			},
			booking: computed(() => {
				return getBooking;
			})
		});

		const rules = {
			modeOfTravel: {
				required
			},
			leavingFrom: {
				v: helpers.withMessage(() => 'Leaving from is required', hasFrom)
			},
			goingTo: {
				v: helpers.withMessage(() => 'Going to is required', hasTo)
			},
			dates: {},
			booking: {
				v: helpers.withMessage(() => {
					return passengerValidationTexts().join('.<br> ');
				}, validPassengers as any)
			}
		};

		const v$ = useVuelidate(rules, state);

		const modeOfTravel = computed(() => state.modeOfTravel);
		watch(modeOfTravel, (current) => {
			selectMode(current);
			if (outwardOptions.value.length < 2) {
				bookingStore.setFromDestination(outwardOptions.value[0].value, bookingStore.getBooking.modeOfTravel);
			}
		});

		const submitForm = async () => {
			
			v$.value.$touch();
			const valid = await v$.value.$validate();

			if (valid) {
				bookingState.value = stateStorage.getItem('booking');
				
				if (props.isExternal === true) {
					quickformRef.value.submit();
				}
				else {
				
					loading.value = true;
					nextTick(() => {
						quickformRef.value.submit();
					});
				}
			}
		};

		const error = computed(() => {
			if (v$.value.$errors.length > 0) {
				return v$.value.$errors[0].$message;
			}
			return '';
		});

		getRuntimeConfig().then((c) => quickformAction.value = props.isExternal !== true ? `${c.bookUrl}?step=2` : c.bookUrl);

		const leavingFrom = ref();
		const goingTo = ref();
		const calendar = ref();

		const open = (ref: any) => {
			let next = ref;
			let count = 0;

			nextTick(() => {
				while (
					next.options &&
					next.options.filter((o: any) => !o.disabled).length < 2 &&
					count < 4 //to prevent an infinite loop
				) {
					count++;
					//no options go to next
					next = next.next;
				}

				const selectEl = next.$el.querySelector('.multiselect');
				if (next.open && selectEl) {
					selectEl.focus();
					next.open();
				} else {
					next.$el.focus();
				}
			});
		};

		return {
			typeOfTravel,
			v$,
			from,
			to,
			returnOptions,
			outwardOptions,
			submitForm,
			error,
			isMediumScreen,
			isSmallScreen,
			loading,
			open,
			leavingFrom,
			goingTo,
			calendar,
			quickformRef,
			bookingState,
			quickformAction,
			outwardDate,
			returnDate,
			passengers,
			voucherCode,
		};
	}
});
